// ui/hand-summary.js: #15: adapt the Lua snapshot's score_summary into the
// shape EndOfHandModal renders.
//
// The Lua game state stores a compact summary:
//   { delta_team, delta_score, label, game_over }   (state.lua)
// but EndOfHandModal (built against the hot-seat demo states) expects:
//   { result, scoring_team, delta, prev_scores, new_scores, was_loner }
// Passing the Lua shape straight through rendered an empty modal (no
// headline, no score deltas). This pure helper bridges the two so the
// networked (and real hot-seat) round-over shows a real recap.

/** team_a = seats 1 & 3 (you + partner), team_b = seats 2 & 4. */
function teamOfSeat(seat) {
  return (seat === 1 || seat === 3) ? 'team_a' : 'team_b';
}

/**
 * Build the EndOfHandModal `summary` from a game snapshot, or null when the
 * hand hasn't been scored yet. Networked play supplies HAND_SCORED so totals
 * come from its running_scores even when the recap snapshot lags.
 *
 * @param {any} snap  a game_state_snapshot (Lua public state)
 * @param {any} [scoredEvent] HAND_SCORED payload with authoritative running_scores
 * @param {{ requireScoredEvent?: boolean }} [opts] networked play: without
 *   the HAND_SCORED totals (a reattach mid-recap), the snapshot's scores can
 *   lag, so prev_scores and new_scores are null and the recap hides them.
 * @returns {{
 *   result: 'made_it'|'march'|'loner_sweep'|'loner_partial'|'euchred',
 *   scoring_team: 'team_a'|'team_b',
 *   delta: number,
 *   prev_scores: { team_a: number, team_b: number } | null,
 *   new_scores: { team_a: number, team_b: number } | null,
 *   was_loner: boolean,
 *   label: string|undefined,
 * } | null}
 */
export function handSummaryFromSnapshot(snap, scoredEvent = null, opts = {}) {
  const ss = snap?.score_summary;
  if (!ss || !ss.delta_team || typeof ss.delta_score !== 'number') return null;

  const scoringTeam = ss.delta_team;
  const delta = scoredEvent?.score_delta ?? ss.delta_score;
  const scores = scoredEvent?.running_scores ?? snap.scores;
  const newScores = {
    team_a: scores?.team_a ?? 0,
    team_b: scores?.team_b ?? 0,
  };
  // Back out the event's score delta to get the pre-hand totals.
  const prevScores = { ...newScores, [scoringTeam]: newScores[scoringTeam] - delta };
  const wasLoner = !!snap.caller_went_alone;
  const makerTeam = snap.maker_seat ? teamOfSeat(snap.maker_seat) : scoringTeam;

  /** @type {'made_it'|'march'|'loner_sweep'|'loner_partial'|'euchred'} */
  let result;
  if (scoringTeam !== makerTeam) {
    // The defenders scored → the makers were set.
    result = 'euchred';
  } else if (wasLoner && delta >= 4) {
    result = 'loner_sweep';      // alone + all five tricks (+4)
  } else if (wasLoner) {
    result = 'loner_partial';    // alone, made it but not a sweep (+1)
  } else if (delta >= 2) {
    result = 'march';            // all five tricks with a partner (+2)
  } else {
    result = 'made_it';          // made the bid (+1)
  }

  const trusted = !opts.requireScoredEvent || !!scoredEvent?.running_scores;
  return {
    result,
    scoring_team: scoringTeam,
    delta,
    prev_scores: trusted ? prevScores : null,
    new_scores: trusted ? newScores : null,
    was_loner: wasLoner,
    label: ss.label,
  };
}
